import {
  GripHorizontal,
  Loader2,
  Menu,
  MessageSquare,
  MoreHorizontal,
  Move,
  PanelBottom,
  PanelLeft,
  PanelRight,
  Pencil,
  RotateCcw,
  Search,
  Share,
  SquarePen,
  Trash2,
  X,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { useNavigate } from "react-router-dom";
import { logger } from "../../common/logger";
import {
  ConversationSummary,
  renameConversation,
  deleteConversation,
  shareConversation,
  ShareResponse,
} from "../../api/conversationHistoryService";
import { SystemLanguage } from "../../common/system-language";
import { ShareModal } from "./ShareModal";

interface SidebarProps {
  conversations: ConversationSummary[];
  currentSessionId: string | null;
  onSelectConversation: (sessionId: string) => void;
  onNewConversation: () => void;
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
  loading?: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  onConversationDeleted: (sessionId: string) => void;
  onConversationRenamed: (sessionId: string, title: string) => void;
  systemLanguage: SystemLanguage;
  isMobile?: boolean;
}

type PanelMode = "left" | "right" | "bottom" | "floating";
type ResizeDirection =
  | "n"
  | "s"
  | "e"
  | "w"
  | "ne"
  | "nw"
  | "se"
  | "sw";
type DockPreview = Exclude<PanelMode, "floating"> | null;

interface PanelLayoutState {
  mode: PanelMode;
  floating: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  docked: {
    leftWidth: number;
    rightWidth: number;
    bottomHeight: number;
  };
}

interface ViewportMetrics {
  width: number;
  height: number;
}

interface PanelConstraints {
  minFloatingWidth: number;
  maxFloatingWidth: number;
  minFloatingHeight: number;
  maxFloatingHeight: number;
  minDockWidth: number;
  maxDockWidth: number;
  minBottomHeight: number;
  maxBottomHeight: number;
}

interface ActiveInteraction {
  type: "drag" | "resize";
  pointerId: number;
  pointerType: string;
  startX: number;
  startY: number;
  startLayout: PanelLayoutState;
  floatingOffsetX?: number;
  floatingOffsetY?: number;
  resizeDirection?: ResizeDirection;
  captureElement: HTMLElement | null;
}

const DESKTOP_STORAGE_KEY = "avatar-conversation-sidebar-layout-desktop-v1";
const DESKTOP_DEFAULT_LAYOUT: PanelLayoutState = {
  mode: "left",
  floating: { x: 1548, y: 96, width: 360, height: 342 },
  docked: { leftWidth: 320, rightWidth: 320, bottomHeight: 320 },
};
const MOBILE_STORAGE_KEY = "avatar-conversation-sidebar-layout-mobile-v1";
const HEADER_OFFSET = 72;
const SIDE_GAP = 12;
const BOTTOM_GAP = 12;
const DOCK_PREVIEW_THRESHOLD = 64;
const EDGE_HANDLE_SIZE = 10;
const CORNER_HANDLE_SIZE = 14;
const FLOATING_PANEL_Z_INDEX = 80;
const COLLAPSED_PANEL_Z_INDEX = 60;

function getViewportMetrics(): ViewportMetrics {
  if (typeof window === "undefined") {
    return { width: 1440, height: 900 };
  }

  return {
    width: window.innerWidth,
    height: window.visualViewport?.height ?? window.innerHeight,
  };
}

function getConstraints(viewport: ViewportMetrics, isMobile: boolean): PanelConstraints {
  const maxUsableWidth = Math.max(240, viewport.width - SIDE_GAP * 2);
  const maxUsableHeight = Math.max(200, viewport.height - HEADER_OFFSET - BOTTOM_GAP);
  const minFloatingWidth = Math.min(280, maxUsableWidth);
  const minFloatingHeight = Math.min(200, maxUsableHeight);

  return {
    minFloatingWidth,
    maxFloatingWidth: Math.max(
      minFloatingWidth,
      Math.min(maxUsableWidth, Math.floor(viewport.width * 0.9))
    ),
    minFloatingHeight,
    maxFloatingHeight: Math.max(
      minFloatingHeight,
      Math.min(maxUsableHeight, Math.floor(maxUsableHeight * 0.85))
    ),
    minDockWidth: Math.min(isMobile ? maxUsableWidth : 280, maxUsableWidth),
    maxDockWidth: Math.max(280, Math.min(560, viewport.width - 64)),
    minBottomHeight: Math.min(220, maxUsableHeight),
    maxBottomHeight: Math.max(
      220,
      Math.min(maxUsableHeight, Math.floor(maxUsableHeight * 0.85))
    ),
  };
}

function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) return min;
  if (max < min) return min;
  return Math.min(Math.max(value, min), max);
}

function createDefaultLayout(isMobile: boolean, viewport: ViewportMetrics): PanelLayoutState {
  if (!isMobile) {
    return normalizeLayout(DESKTOP_DEFAULT_LAYOUT, viewport, isMobile);
  }

  const constraints = getConstraints(viewport, isMobile);
  const defaultDockWidth = clamp(
    Math.min(320, Math.floor(viewport.width * 0.32)),
    constraints.minDockWidth,
    constraints.maxDockWidth
  );
  const defaultBottomHeight = clamp(
    Math.min(isMobile ? 360 : 320, Math.floor((viewport.height - HEADER_OFFSET) * (isMobile ? 0.5 : 0.4))),
    constraints.minBottomHeight,
    constraints.maxBottomHeight
  );
  const defaultFloatingWidth = clamp(
    Math.min(360, Math.floor(viewport.width * 0.4)),
    constraints.minFloatingWidth,
    constraints.maxFloatingWidth
  );
  const defaultFloatingHeight = clamp(
    Math.min(440, Math.floor((viewport.height - HEADER_OFFSET) * 0.6)),
    constraints.minFloatingHeight,
    constraints.maxFloatingHeight
  );

  return {
    mode: isMobile ? "bottom" : "right",
    floating: {
      x: clamp(viewport.width - defaultFloatingWidth - SIDE_GAP, SIDE_GAP, viewport.width - defaultFloatingWidth - SIDE_GAP),
      y: clamp(
        HEADER_OFFSET + 24,
        HEADER_OFFSET,
        viewport.height - defaultFloatingHeight - BOTTOM_GAP
      ),
      width: defaultFloatingWidth,
      height: defaultFloatingHeight,
    },
    docked: {
      leftWidth: defaultDockWidth,
      rightWidth: defaultDockWidth,
      bottomHeight: defaultBottomHeight,
    },
  };
}

function normalizeLayout(
  input: PanelLayoutState,
  viewport: ViewportMetrics,
  isMobile: boolean
): PanelLayoutState {
  const constraints = getConstraints(viewport, isMobile);
  const nextMode = input.mode;
  const width = clamp(input.floating.width, constraints.minFloatingWidth, constraints.maxFloatingWidth);
  const height = clamp(input.floating.height, constraints.minFloatingHeight, constraints.maxFloatingHeight);
  const maxX = Math.max(SIDE_GAP, viewport.width - width - SIDE_GAP);
  const maxY = Math.max(HEADER_OFFSET, viewport.height - height - BOTTOM_GAP);

  return {
    mode: nextMode,
    floating: {
      x: clamp(input.floating.x, SIDE_GAP, maxX),
      y: clamp(input.floating.y, HEADER_OFFSET, maxY),
      width,
      height,
    },
    docked: {
      leftWidth: clamp(input.docked.leftWidth, constraints.minDockWidth, constraints.maxDockWidth),
      rightWidth: clamp(input.docked.rightWidth, constraints.minDockWidth, constraints.maxDockWidth),
      bottomHeight: clamp(
        input.docked.bottomHeight,
        constraints.minBottomHeight,
        constraints.maxBottomHeight
      ),
    },
  };
}

function loadStoredLayout(storageKey: string): PanelLayoutState | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    return JSON.parse(raw) as PanelLayoutState;
  } catch {
    return null;
  }
}

function getDockPreview(clientX: number, clientY: number, viewport: ViewportMetrics): DockPreview {
  if (clientY >= viewport.height - DOCK_PREVIEW_THRESHOLD) return "bottom";
  if (clientX <= DOCK_PREVIEW_THRESHOLD) return "left";
  if (clientX >= viewport.width - DOCK_PREVIEW_THRESHOLD) return "right";
  return null;
}

function getResizeCursor(direction: ResizeDirection): CSSProperties["cursor"] {
  switch (direction) {
    case "n":
    case "s":
      return "row-resize";
    case "e":
    case "w":
      return "col-resize";
    case "ne":
    case "sw":
      return "nesw-resize";
    case "nw":
    case "se":
      return "nwse-resize";
    default:
      return "default";
  }
}

export default function ConversationSidebar({
  conversations,
  currentSessionId,
  onSelectConversation,
  isOpen,
  loading,
  onClose,
  onOpen,
  onNewConversation,
  hasMore,
  isLoadingMore,
  onLoadMore,
  onConversationDeleted,
  onConversationRenamed,
  systemLanguage,
  isMobile = false,
}: SidebarProps) {
  const navigate = useNavigate();
  const isRtl = systemLanguage === "ar";
  const [showCollapsedActions, setShowCollapsedActions] = useState(!isOpen);
  const [localConversations, setLocalConversations] = useState<ConversationSummary[]>(conversations);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>("");
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareData, setShareData] = useState<ShareResponse | null>(null);
  const [viewport, setViewport] = useState<ViewportMetrics>(() => getViewportMetrics());
  const storageKey = isMobile ? MOBILE_STORAGE_KEY : DESKTOP_STORAGE_KEY;
  const [panelLayout, setPanelLayout] = useState<PanelLayoutState>(() =>
    normalizeLayout(
      loadStoredLayout(storageKey) ?? createDefaultLayout(isMobile, getViewportMetrics()),
      getViewportMetrics(),
      isMobile
    )
  );
  const [dockPreview, setDockPreview] = useState<DockPreview>(null);

  const panelRef = useRef<HTMLElement | null>(null);
  const panelLayoutRef = useRef(panelLayout);
  const dockPreviewRef = useRef<DockPreview>(dockPreview);
  const interactionRef = useRef<ActiveInteraction | null>(null);
  const frameRef = useRef<number | null>(null);
  const hydratedKeyRef = useRef<string | null>(null);

  const constraints = useMemo(() => getConstraints(viewport, isMobile), [viewport, isMobile]);

  useEffect(() => {
    panelLayoutRef.current = panelLayout;
  }, [panelLayout]);

  useEffect(() => {
    dockPreviewRef.current = dockPreview;
  }, [dockPreview]);

  useEffect(() => {
    setLocalConversations(conversations);
  }, [conversations]);

  useEffect(() => {
    if (isOpen) {
      setShowCollapsedActions(false);
      return;
    }

    const timeout = setTimeout(() => setShowCollapsedActions(true), 200);
    return () => clearTimeout(timeout);
  }, [isOpen]);

  useEffect(() => {
    const close = () => setOpenMenuId(null);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpenMenuId(null);
        setEditingId(null);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const applyLayout = useCallback((next: PanelLayoutState) => {
    const normalized = normalizeLayout(next, getViewportMetrics(), isMobile);
    panelLayoutRef.current = normalized;

    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
    }

    frameRef.current = window.requestAnimationFrame(() => {
      setPanelLayout(normalized);
      frameRef.current = null;
    });
  }, [isMobile]);

  const resetLayout = useCallback(() => {
    const defaults = normalizeLayout(createDefaultLayout(isMobile, getViewportMetrics()), getViewportMetrics(), isMobile);
    setDockPreview(null);
    applyLayout(defaults);
  }, [applyLayout, isMobile]);

  useEffect(() => {
    const metrics = getViewportMetrics();
    setViewport(metrics);

    const stored = loadStoredLayout(storageKey);
    const nextLayout = normalizeLayout(
      stored ?? createDefaultLayout(isMobile, metrics),
      metrics,
      isMobile
    );

    hydratedKeyRef.current = storageKey;
    setPanelLayout(nextLayout);
    panelLayoutRef.current = nextLayout;
  }, [storageKey, isMobile]);

  useEffect(() => {
    if (hydratedKeyRef.current !== storageKey || typeof window === "undefined") return;

    window.localStorage.setItem(storageKey, JSON.stringify(panelLayout));
  }, [panelLayout, storageKey]);

  useEffect(() => {
    const handleResize = () => {
      const metrics = getViewportMetrics();
      setViewport(metrics);
      const next = normalizeLayout(panelLayoutRef.current, metrics, isMobile);
      panelLayoutRef.current = next;
      setPanelLayout(next);
    };

    window.addEventListener("resize", handleResize);
    window.visualViewport?.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.visualViewport?.removeEventListener("resize", handleResize);
    };
  }, [isMobile]);

  const endInteraction = useCallback((commitDockPreview: boolean) => {
    const interaction = interactionRef.current;
    if (!interaction) return;

    if (interaction.captureElement && interaction.pointerId !== undefined) {
      try {
        interaction.captureElement.releasePointerCapture(interaction.pointerId);
      } catch {
        // noop
      }
    }

    if (commitDockPreview && dockPreviewRef.current) {
      const current = panelLayoutRef.current;
      const nextMode = dockPreviewRef.current;
      if (nextMode) {
        applyLayout({
          ...current,
          mode: nextMode,
        });
      }
    }

    interactionRef.current = null;
    setDockPreview(null);
  }, [applyLayout]);

  useEffect(() => {
    const handlePointerMove = (event: PointerEvent) => {
      const interaction = interactionRef.current;
      if (!interaction || event.pointerId !== interaction.pointerId) return;

      const metrics = getViewportMetrics();
      const start = interaction.startLayout;
      const dx = event.clientX - interaction.startX;
      const dy = event.clientY - interaction.startY;

      if (interaction.type === "drag") {
        const width = start.floating.width;
        const height = start.floating.height;
        const nextX = clamp(
          event.clientX - (interaction.floatingOffsetX ?? 0),
          SIDE_GAP,
          metrics.width - width - SIDE_GAP
        );
        const nextY = clamp(
          event.clientY - (interaction.floatingOffsetY ?? 0),
          HEADER_OFFSET,
          metrics.height - height - BOTTOM_GAP
        );
        const preview = getDockPreview(event.clientX, event.clientY, metrics);
        if (preview !== dockPreviewRef.current) {
          setDockPreview(preview);
        }

        applyLayout({
          ...start,
          mode: "floating",
          floating: {
            ...start.floating,
            x: nextX,
            y: nextY,
          },
        });
        return;
      }

      if (interaction.type === "resize" && interaction.resizeDirection) {
        const dir = interaction.resizeDirection;
        const current = { ...start };

        if (start.mode === "floating") {
          let nextX = start.floating.x;
          let nextY = start.floating.y;
          let nextWidth = start.floating.width;
          let nextHeight = start.floating.height;

          if (dir.includes("e")) {
            nextWidth = clamp(
              start.floating.width + dx,
              constraints.minFloatingWidth,
              constraints.maxFloatingWidth
            );
          }

          if (dir.includes("s")) {
            nextHeight = clamp(
              start.floating.height + dy,
              constraints.minFloatingHeight,
              constraints.maxFloatingHeight
            );
          }

          if (dir.includes("w")) {
            const proposedWidth = clamp(
              start.floating.width - dx,
              constraints.minFloatingWidth,
              constraints.maxFloatingWidth
            );
            nextX = clamp(
              start.floating.x + (start.floating.width - proposedWidth),
              SIDE_GAP,
              metrics.width - proposedWidth - SIDE_GAP
            );
            nextWidth = proposedWidth;
          }

          if (dir.includes("n")) {
            const proposedHeight = clamp(
              start.floating.height - dy,
              constraints.minFloatingHeight,
              constraints.maxFloatingHeight
            );
            nextY = clamp(
              start.floating.y + (start.floating.height - proposedHeight),
              HEADER_OFFSET,
              metrics.height - proposedHeight - BOTTOM_GAP
            );
            nextHeight = proposedHeight;
          }

          applyLayout({
            ...current,
            floating: {
              x: nextX,
              y: nextY,
              width: nextWidth,
              height: nextHeight,
            },
          });
          return;
        }

        if (start.mode === "left") {
          applyLayout({
            ...current,
            docked: {
              ...current.docked,
              leftWidth: clamp(
                event.clientX - SIDE_GAP,
                constraints.minDockWidth,
                constraints.maxDockWidth
              ),
            },
          });
          return;
        }

        if (start.mode === "right") {
          applyLayout({
            ...current,
            docked: {
              ...current.docked,
              rightWidth: clamp(
                metrics.width - event.clientX - SIDE_GAP,
                constraints.minDockWidth,
                constraints.maxDockWidth
              ),
            },
          });
          return;
        }

        applyLayout({
          ...current,
          docked: {
            ...current.docked,
            bottomHeight: clamp(
              metrics.height - event.clientY - BOTTOM_GAP,
              constraints.minBottomHeight,
              constraints.maxBottomHeight
            ),
          },
        });
      }
    };

    const handlePointerUp = (event: PointerEvent) => {
      const interaction = interactionRef.current;
      if (!interaction || event.pointerId !== interaction.pointerId) return;
      endInteraction(true);
    };

    const handlePointerCancel = (event: PointerEvent) => {
      const interaction = interactionRef.current;
      if (!interaction || event.pointerId !== interaction.pointerId) return;
      endInteraction(false);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerCancel);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerCancel);
    };
  }, [applyLayout, constraints, endInteraction]);

  useEffect(() => {
    return () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
      }
      endInteraction(false);
    };
  }, [endInteraction]);

  const beginDrag = useCallback((event: React.PointerEvent<HTMLElement>) => {
    if (!isOpen) return;

    const target = event.currentTarget;
    const panelRect = panelRef.current?.getBoundingClientRect();
    if (!panelRect) return;

    event.preventDefault();
    event.stopPropagation();

    const baseFloating = {
      x: panelRect.left,
      y: panelRect.top,
      width: panelRect.width,
      height: panelRect.height,
    };
    const startLayout = normalizeLayout(
      {
        ...panelLayoutRef.current,
        mode: "floating",
        floating: baseFloating,
      },
      getViewportMetrics(),
      isMobile
    );

    try {
      target.setPointerCapture(event.pointerId);
    } catch {
      // noop
    }

    interactionRef.current = {
      type: "drag",
      pointerId: event.pointerId,
      pointerType: event.pointerType,
      startX: event.clientX,
      startY: event.clientY,
      startLayout,
      floatingOffsetX: event.clientX - panelRect.left,
      floatingOffsetY: event.clientY - panelRect.top,
      captureElement: target,
    };

    setDockPreview(null);
    applyLayout(startLayout);
  }, [applyLayout, isMobile, isOpen]);

  const beginResize = useCallback(
    (event: React.PointerEvent<HTMLElement>, resizeDirection: ResizeDirection) => {
      if (!isOpen) return;
      event.preventDefault();
      event.stopPropagation();

      const target = event.currentTarget;
      try {
        target.setPointerCapture(event.pointerId);
      } catch {
        // noop
      }

      interactionRef.current = {
        type: "resize",
        pointerId: event.pointerId,
        pointerType: event.pointerType,
        startX: event.clientX,
        startY: event.clientY,
        startLayout: panelLayoutRef.current,
        resizeDirection,
        captureElement: target,
      };
    },
    [isOpen]
  );

  const setDockMode = useCallback(
    (mode: PanelMode) => {
      applyLayout({
        ...panelLayoutRef.current,
        mode,
      });
    },
    [applyLayout]
  );

  const startRename = (conv: ConversationSummary) => {
    setEditingId(conv.sessionId);
    setEditValue(conv.title ?? "");
  };

  const commitRename = async (sessionId: string) => {
    const next = editValue.trim();
    if (!next) {
      setEditingId(null);
      setEditValue("");
      return;
    }

    try {
      await renameConversation(sessionId, next);
      setLocalConversations((prev) =>
        prev.map((c) => (c.sessionId === sessionId ? { ...c, title: next } : c))
      );
      onConversationRenamed(sessionId, next);
      setEditingId(null);
      setEditValue("");
    } catch (err) {
      logger.error("Rename chat error", err);
    }
  };

  const cancelRename = () => {
    setEditingId(null);
    setEditValue("");
  };

  const deleteConv = async (sessionId: string) => {
    try {
      await deleteConversation(sessionId);
      setLocalConversations((prev) => prev.filter((c) => c.sessionId !== sessionId));
      onConversationDeleted(sessionId);
    } catch (err) {
      logger.error("Delete chat error", err);
    }
  };

  const handleShare = async (conv: ConversationSummary) => {
    setShareModalOpen(true);
    setShareLoading(true);
    try {
      const data = await shareConversation(conv.sessionId);
      setShareData(data);
    } catch (err) {
      logger.error("Share error", err);
    } finally {
      setShareLoading(false);
    }
  };

  const t = (key: string) => {
    const translations: Record<string, Record<string, string>> = {
      en: {
        "sidebar.search": "Search",
        "sidebar.newConversation": "New Conversation",
        "sidebar.noConversations": "No conversations",
        "sidebar.chats": "Chats",
        "sidebar.poweredBy": "POWERED BY",
        "sidebar.closeSidebar": "Close sidebar",
        "sidebar.openSidebar": "Open sidebar",
        "sidebar.newChat": "New Chat",
        "sidebar.drag": "Drag sidebar",
        "sidebar.dockLeft": "Dock left",
        "sidebar.dockRight": "Dock right",
        "sidebar.dockBottom": "Dock bottom",
        "sidebar.floating": "Floating mode",
        "sidebar.resetPanel": "Reset position and size",
        more: "More",
        share: "Share",
        rename: "Rename",
        delete: "Delete",
      },
      ar: {
        "sidebar.search": "بحث",
        "sidebar.newConversation": "محادثة جديدة",
        "sidebar.noConversations": "لا توجد محادثات",
        "sidebar.chats": "المحادثات",
        "sidebar.poweredBy": "مدعوم من",
        "sidebar.closeSidebar": "إغلاق الشريط الجانبي",
        "sidebar.openSidebar": "فتح الشريط الجانبي",
        "sidebar.newChat": "محادثة جديدة",
        "sidebar.drag": "سحب الشريط الجانبي",
        "sidebar.dockLeft": "تثبيت يسارًا",
        "sidebar.dockRight": "تثبيت يمينًا",
        "sidebar.dockBottom": "تثبيت أسفل",
        "sidebar.floating": "وضع عائم",
        "sidebar.resetPanel": "إعادة ضبط الموضع والحجم",
        more: "المزيد",
        share: "مشاركة",
        rename: "إعادة تسمية",
        delete: "حذف",
      },
    };

    return translations[systemLanguage]?.[key] || translations.en[key] || key;
  };

  const panelStyle = useMemo<CSSProperties>(() => {
    const maxHeightExpr = `calc(100dvh - ${HEADER_OFFSET + BOTTOM_GAP}px - env(safe-area-inset-bottom))`;

    if (panelLayout.mode === "left") {
      return {
        position: "fixed",
        top: HEADER_OFFSET,
        left: SIDE_GAP,
        width: panelLayout.docked.leftWidth,
        height: `calc(100dvh - ${HEADER_OFFSET + BOTTOM_GAP}px - env(safe-area-inset-bottom))`,
        maxHeight: maxHeightExpr,
        zIndex: FLOATING_PANEL_Z_INDEX,
      };
    }

    if (panelLayout.mode === "right") {
      return {
        position: "fixed",
        top: HEADER_OFFSET,
        right: SIDE_GAP,
        width: panelLayout.docked.rightWidth,
        height: `calc(100dvh - ${HEADER_OFFSET + BOTTOM_GAP}px - env(safe-area-inset-bottom))`,
        maxHeight: maxHeightExpr,
        zIndex: FLOATING_PANEL_Z_INDEX,
      };
    }

    if (panelLayout.mode === "bottom") {
      return {
        position: "fixed",
        left: isMobile ? 0 : SIDE_GAP,
        right: isMobile ? 0 : SIDE_GAP,
        bottom: `calc(${BOTTOM_GAP}px + env(safe-area-inset-bottom))`,
        height: panelLayout.docked.bottomHeight,
        maxHeight: maxHeightExpr,
        zIndex: FLOATING_PANEL_Z_INDEX,
      };
    }

    return {
      position: "fixed",
      left: panelLayout.floating.x,
      top: panelLayout.floating.y,
      width: panelLayout.floating.width,
      height: panelLayout.floating.height,
      maxWidth: "90vw",
      maxHeight: maxHeightExpr,
      zIndex: FLOATING_PANEL_Z_INDEX,
    };
  }, [isMobile, panelLayout]);

  const previewStyle = useMemo<CSSProperties | null>(() => {
    if (!dockPreview) return null;

    if (dockPreview === "left") {
      return {
        position: "fixed",
        top: HEADER_OFFSET,
        left: SIDE_GAP,
        width: clamp(panelLayout.docked.leftWidth, constraints.minDockWidth, constraints.maxDockWidth),
        height: `calc(100dvh - ${HEADER_OFFSET + BOTTOM_GAP}px - env(safe-area-inset-bottom))`,
      };
    }

    if (dockPreview === "right") {
      return {
        position: "fixed",
        top: HEADER_OFFSET,
        right: SIDE_GAP,
        width: clamp(panelLayout.docked.rightWidth, constraints.minDockWidth, constraints.maxDockWidth),
        height: `calc(100dvh - ${HEADER_OFFSET + BOTTOM_GAP}px - env(safe-area-inset-bottom))`,
      };
    }

    return {
      position: "fixed",
      left: isMobile ? 0 : SIDE_GAP,
      right: isMobile ? 0 : SIDE_GAP,
      bottom: `calc(${BOTTOM_GAP}px + env(safe-area-inset-bottom))`,
      height: clamp(
        panelLayout.docked.bottomHeight,
        constraints.minBottomHeight,
        constraints.maxBottomHeight
      ),
    };
  }, [constraints, dockPreview, isMobile, panelLayout.docked]);

  const rootPanelClasses = [
    "sidebar-gray",
    "app-surface",
    "border",
    "border-app",
    "shadow-[0_20px_60px_rgba(15,23,42,0.18)]",
    "overflow-hidden",
    "flex",
    "flex-col",
    panelLayout.mode === "bottom" || isMobile ? "rounded-t-3xl rounded-b-none" : "rounded-2xl",
  ].join(" ");

  const contentWrapperClass = [
    "flex-1",
    "min-h-0",
    "overflow-hidden",
    "flex",
    "flex-col",
  ].join(" ");

  const headerButtonClass =
    "h-8 w-8 rounded-lg border border-app bg-white/80 text-[#5d6372] hover:bg-white hover:text-[#1a1a2e] transition-colors inline-flex items-center justify-center";

  const renderFloatingResizeHandle = (direction: ResizeDirection, style: CSSProperties) => (
    <div
      key={direction}
      role="presentation"
      onPointerDown={(event) => beginResize(event, direction)}
      className="absolute z-20"
      style={{
        ...style,
        cursor: getResizeCursor(direction),
        touchAction: "none",
      }}
    />
  );

  return (
    <>
      {previewStyle && (
        <div
          aria-hidden="true"
          className="pointer-events-none rounded-[24px] border border-[#3333ff]/25 bg-[#3333ff]/10"
          style={{ ...previewStyle, zIndex: FLOATING_PANEL_Z_INDEX - 1 }}
        />
      )}

      {isOpen && (
        <aside
          ref={panelRef}
          className={rootPanelClasses}
          style={panelStyle}
        >
          {panelLayout.mode === "floating" && (
            <>
              {renderFloatingResizeHandle("n", {
                top: -EDGE_HANDLE_SIZE / 2,
                left: CORNER_HANDLE_SIZE,
                right: CORNER_HANDLE_SIZE,
                height: EDGE_HANDLE_SIZE,
              })}
              {renderFloatingResizeHandle("s", {
                bottom: -EDGE_HANDLE_SIZE / 2,
                left: CORNER_HANDLE_SIZE,
                right: CORNER_HANDLE_SIZE,
                height: EDGE_HANDLE_SIZE,
              })}
              {renderFloatingResizeHandle("e", {
                top: CORNER_HANDLE_SIZE,
                right: -EDGE_HANDLE_SIZE / 2,
                bottom: CORNER_HANDLE_SIZE,
                width: EDGE_HANDLE_SIZE,
              })}
              {renderFloatingResizeHandle("w", {
                top: CORNER_HANDLE_SIZE,
                left: -EDGE_HANDLE_SIZE / 2,
                bottom: CORNER_HANDLE_SIZE,
                width: EDGE_HANDLE_SIZE,
              })}
              {renderFloatingResizeHandle("nw", {
                top: -CORNER_HANDLE_SIZE / 2,
                left: -CORNER_HANDLE_SIZE / 2,
                width: CORNER_HANDLE_SIZE,
                height: CORNER_HANDLE_SIZE,
              })}
              {renderFloatingResizeHandle("ne", {
                top: -CORNER_HANDLE_SIZE / 2,
                right: -CORNER_HANDLE_SIZE / 2,
                width: CORNER_HANDLE_SIZE,
                height: CORNER_HANDLE_SIZE,
              })}
              {renderFloatingResizeHandle("sw", {
                bottom: -CORNER_HANDLE_SIZE / 2,
                left: -CORNER_HANDLE_SIZE / 2,
                width: CORNER_HANDLE_SIZE,
                height: CORNER_HANDLE_SIZE,
              })}
              {renderFloatingResizeHandle("se", {
                bottom: -CORNER_HANDLE_SIZE / 2,
                right: -CORNER_HANDLE_SIZE / 2,
                width: CORNER_HANDLE_SIZE,
                height: CORNER_HANDLE_SIZE,
              })}
            </>
          )}

          {panelLayout.mode === "left" && (
            <div
              role="presentation"
              onPointerDown={(event) => beginResize(event, "e")}
              className="absolute top-0 right-0 h-full"
              style={{ width: EDGE_HANDLE_SIZE, cursor: "col-resize", touchAction: "none" }}
            />
          )}

          {panelLayout.mode === "right" && (
            <div
              role="presentation"
              onPointerDown={(event) => beginResize(event, "w")}
              className="absolute top-0 left-0 h-full"
              style={{ width: EDGE_HANDLE_SIZE, cursor: "col-resize", touchAction: "none" }}
            />
          )}

          {panelLayout.mode === "bottom" && (
            <div
              role="presentation"
              onPointerDown={(event) => beginResize(event, "n")}
              className="absolute top-0 left-0 w-full"
              style={{ height: EDGE_HANDLE_SIZE, cursor: "row-resize", touchAction: "none" }}
            />
          )}

          <div className="border-b border-app px-3 pt-3 pb-2 bg-white/55 backdrop-blur-sm">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onPointerDown={beginDrag}
                aria-label={t("sidebar.drag")}
                title={t("sidebar.drag")}
                className="flex min-w-0 flex-1 items-center gap-2 rounded-xl border border-app bg-white/80 px-3 py-2 text-[#5d6372] hover:bg-white active:cursor-grabbing"
                style={{ cursor: panelLayout.mode === "floating" ? "grab" : "move", touchAction: "none" }}
              >
                <GripHorizontal size={16} />
                <span className="truncate text-sm font-medium text-[#1a1a2e]">
                  {t("sidebar.chats")}
                </span>
              </button>

              <div className="flex items-center gap-1">
                <button
                  type="button"
                  aria-label={t("sidebar.dockLeft")}
                  title={t("sidebar.dockLeft")}
                  onClick={() => setDockMode("left")}
                  className={headerButtonClass}
                >
                  <PanelLeft size={16} />
                </button>
                <button
                  type="button"
                  aria-label={t("sidebar.dockRight")}
                  title={t("sidebar.dockRight")}
                  onClick={() => setDockMode("right")}
                  className={headerButtonClass}
                >
                  <PanelRight size={16} />
                </button>
                <button
                  type="button"
                  aria-label={t("sidebar.dockBottom")}
                  title={t("sidebar.dockBottom")}
                  onClick={() => setDockMode("bottom")}
                  className={headerButtonClass}
                >
                  <PanelBottom size={16} />
                </button>
                <button
                  type="button"
                  aria-label={t("sidebar.floating")}
                  title={t("sidebar.floating")}
                  onClick={() => setDockMode("floating")}
                  className={headerButtonClass}
                >
                  <Move size={16} />
                </button>
                <button
                  type="button"
                  aria-label={t("sidebar.resetPanel")}
                  title={t("sidebar.resetPanel")}
                  onClick={resetLayout}
                  className={headerButtonClass}
                >
                  <RotateCcw size={16} />
                </button>
                <button
                  type="button"
                  aria-label={t("sidebar.closeSidebar")}
                  title={t("sidebar.closeSidebar")}
                  onClick={onClose}
                  className={headerButtonClass}
                >
                  <Menu size={16} />
                </button>
              </div>
            </div>
          </div>

          <div className={contentWrapperClass}>
            <div className="px-3 pt-3 pb-2">
              <button
                onClick={() => setShowSearchModal(true)}
                className="w-full flex items-center gap-2 px-3 py-2 transition-colors font-medium rounded-lg hover:bg-gray-100 text-[#80868E]"
              >
                <Search size={20} className="text-[#80868E]" />
                <span className="text-[#80868E]">{t("sidebar.search")}</span>
              </button>

              <button
                onClick={() => {
                  onNewConversation();
                }}
                className="mt-1 w-full flex items-center gap-2 px-3 py-2 transition-colors font-medium rounded-lg hover:bg-gray-100 text-[#80868E]"
              >
                <SquarePen size={20} className="text-[#80868E]" />
                <span className="text-[#80868E]">{t("sidebar.newConversation")}</span>
              </button>
            </div>

            <div
              className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden sidebar-scroll relative"
              onScroll={(e) => {
                const el = e.currentTarget;
                const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;

                if (distanceToBottom < 80 && hasMore && !isLoadingMore) {
                  onLoadMore();
                }
              }}
            >
              {loading && (
                <div className="absolute inset-0 flex justify-center bg-white/60 z-50">
                  <Loader2 className="h-5 w-5 text-[#80868E] animate-spin" />
                </div>
              )}

              {!loading &&
                (localConversations.length === 0 ? (
                  <div className="p-4 text-center text-black-400">
                    <MessageSquare size={48} className="mx-auto mb-2 opacity-50" />
                    <p className="text-sm">{t("sidebar.noConversations")}</p>
                  </div>
                ) : (
                  <div className="px-4 pt-1 pb-4">
                    <p className={`text-[#80868E] font-bold px-2 py-1 ${isRtl ? "text-right" : "text-left"}`}>
                      {t("sidebar.chats")}
                    </p>

                    {localConversations.map((conv) => {
                      const isActive = currentSessionId === conv.sessionId;
                      const fontColor = "text-[#80868E]";
                      const bgColor = "bg-[rgba(51,51,255,0.17)]";
                      const isEditing = editingId === conv.sessionId;
                      const isMenuOpen = openMenuId === conv.sessionId;

                      return (
                        <div key={conv.sessionId} className="relative">
                          <div
                            onClick={() => {
                              if (isEditing) return;
                              onSelectConversation(conv.sessionId);
                              navigate(`/chat/${conv.sessionId}`);
                            }}
                            className={`w-full px-3 py-2 rounded-lg mb-1 transition-colors cursor-pointer ${
                              isRtl ? "text-right" : "text-left"
                            } ${
                              isActive ? bgColor + " text-[#33f]" : "hover:bg-gray-100"
                            }`}
                          >
                            <div className="flex items-start gap-2">
                              <div style={{ paddingTop: 4 }} className="flex-1 min-w-0">
                                {!isEditing ? (
                                  <div className="flex items-center gap-2">
                                    <p className={`font-medium text-sm truncate ${fontColor}`}>
                                      {conv.title}
                                    </p>
                                  </div>
                                ) : (
                                  <input
                                    autoFocus
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        e.preventDefault();
                                        commitRename(conv.sessionId);
                                      }
                                      if (e.key === "Escape") {
                                        e.preventDefault();
                                        cancelRename();
                                      }
                                    }}
                                    onBlur={() => {
                                      commitRename(conv.sessionId);
                                    }}
                                    className="w-full text-sm font-medium px-2 py-1 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#33f]/30"
                                  />
                                )}
                              </div>

                              <button
                                aria-label={t("more")}
                                title={t("more")}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenMenuId((prev) => (prev === conv.sessionId ? null : conv.sessionId));
                                }}
                                className="p-1 rounded-md hover:bg-black/5 text-[#80868E]"
                              >
                                <MoreHorizontal size={18} />
                              </button>
                            </div>
                          </div>

                          {isMenuOpen && (
                            <div
                              className={`absolute z-50 -top-1 mt-10 w-40 rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden ${
                                isRtl ? "left-2" : "right-2"
                              }`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                onClick={() => {
                                  setOpenMenuId(null);
                                  handleShare(conv);
                                }}
                                className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 text-gray-700 ${
                                  isRtl ? "flex-row-reverse text-right" : ""
                                }`}
                              >
                                <Share size={16} className="text-gray-500" />
                                {t("share")}
                              </button>

                              <button
                                onClick={() => {
                                  setOpenMenuId(null);
                                  startRename(conv);
                                }}
                                className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 text-gray-700 ${
                                  isRtl ? "flex-row-reverse text-right" : ""
                                }`}
                              >
                                <Pencil size={16} className="text-gray-500" />
                                {t("rename")}
                              </button>

                              <button
                                onClick={() => {
                                  setOpenMenuId(null);
                                  deleteConv(conv.sessionId);
                                }}
                                className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-red-50 text-red-600 ${
                                  isRtl ? "flex-row-reverse text-right" : ""
                                }`}
                              >
                                <Trash2 size={16} className="text-red-500" />
                                {t("delete")}
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {isLoadingMore && (
                      <div className="flex justify-center py-2">
                        <Loader2 className="h-4 w-4 text-[#80868E] animate-spin" />
                      </div>
                    )}
                  </div>
                ))}
            </div>

            <div
              className="border-t border-app app-surface w-full p-4 space-y-3"
              style={{
                paddingBottom: isMobile ? "calc(1rem + env(safe-area-inset-bottom))" : undefined,
              }}
            >
              <div className={`text-xs text-[#80868E] leading-tight ${isRtl ? "text-right" : "text-left"}`}>
                <span>
                  {t("sidebar.poweredBy")}
                  <a
                    href="https://accordbgroup.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#33f] hover:underline font-semibold"
                  >
                    {" "}
                    ABG
                  </a>
                </span>
              </div>
            </div>
          </div>
        </aside>
      )}

      {showCollapsedActions && !isOpen && !isMobile && (
        <div
          className={`fixed ${isRtl ? "right-3" : "left-3"} w-16 app-surface rounded-2xl border border-app flex flex-col items-center py-4`}
          style={{
            top: HEADER_OFFSET,
            bottom: BOTTOM_GAP,
            zIndex: COLLAPSED_PANEL_Z_INDEX,
          }}
        >
          <div className="flex flex-col items-center gap-4">
            <button
              aria-label={t("sidebar.openSidebar")}
              title={t("sidebar.openSidebar")}
              onClick={onOpen}
              className="p-2 text-gray-400 hover:text-gray-200"
            >
              <Menu size={20} className="text-[#80868E]" />
            </button>

            <button
              aria-label={t("sidebar.search")}
              title={t("sidebar.search")}
              onClick={() => setShowSearchModal(true)}
              className="p-2 text-gray-400 hover:text-gray-200"
            >
              <Search size={20} className="text-[#80868E]" />
            </button>

            <button
              aria-label={t("sidebar.newChat")}
              title={t("sidebar.newChat")}
              onClick={() => {
                onNewConversation();
              }}
              className="p-2 text-gray-400 hover:text-gray-200"
            >
              <SquarePen size={20} className="text-[#80868E]" />
            </button>
          </div>

          <div className="mt-auto flex flex-col items-center gap-3">
            <div className="text-[9px] text-[#80868E] text-center leading-tight font-semibold">
              <div>{t("sidebar.poweredBy")}</div>
              <div>
                <a
                  href="https://accordbgroup.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#33f] hover:underline"
                >
                  ABG
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCollapsedActions && !isOpen && isMobile && (
        <div
          className="fixed right-4 flex items-center gap-2 rounded-2xl border border-app bg-white/95 px-3 py-2 shadow-[0_12px_32px_rgba(15,23,42,0.16)]"
          style={{
            bottom: "calc(16px + env(safe-area-inset-bottom))",
            zIndex: COLLAPSED_PANEL_Z_INDEX,
          }}
        >
          <button
            aria-label={t("sidebar.openSidebar")}
            title={t("sidebar.openSidebar")}
            onClick={onOpen}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#3333ff] text-white"
          >
            <Menu size={18} />
          </button>
          <div className="text-sm font-medium text-[#1a1a2e]">{t("sidebar.chats")}</div>
        </div>
      )}

      {showSearchModal && (
        <SearchModal
          conversations={localConversations}
          onClose={() => setShowSearchModal(false)}
          onSelectConversation={(sessionId: string) => {
            onSelectConversation(sessionId);
            navigate(`/chat/${sessionId}`);
            setShowSearchModal(false);
          }}
          systemLanguage={systemLanguage}
        />
      )}

      <ShareModal
        open={shareModalOpen}
        loading={shareLoading}
        data={shareData}
        onClose={() => setShareModalOpen(false)}
        systemLanguage={systemLanguage}
      />
    </>
  );
}

function SearchModal({
  conversations,
  onClose,
  onSelectConversation,
  systemLanguage,
}: {
  conversations: ConversationSummary[];
  onClose: () => void;
  onSelectConversation: (sessionId: string) => void;
  systemLanguage: SystemLanguage;
}) {
  const [query, setQuery] = useState("");
  const isAr = systemLanguage === "ar";

  const results =
    query.trim().length < 2
      ? []
      : conversations.filter((c) =>
          c.title.toLowerCase().includes(query.trim().toLowerCase())
        );

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
      <div className="bg-white w-full max-w-md rounded-xl shadow-lg p-6 relative">
        <button
          onClick={onClose}
          className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
        >
          <X size={18} />
        </button>

        <h2 className="text-lg font-semibold mb-4">{isAr ? "بحث" : "Search"}</h2>

        <input
          className="modal-input w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring focus:ring-blue-200 mb-4"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={isAr ? "اكتب للبحث..." : "Type to search..."}
          autoFocus
        />

        {query.trim().length >= 2 && results.length === 0 && (
          <p className="text-sm text-gray-500 text-center">
            {isAr ? "لا توجد نتائج." : "No results."}
          </p>
        )}

        <ul>
          {results.map((res) => (
            <li key={res.sessionId}>
              <button
                onClick={() => {
                  onSelectConversation(res.sessionId);
                }}
                className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 text-sm"
              >
                {res.title}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
